import { EMPTY, formatDateFull, formatDecimal, formatInteger, isNumber } from './piFormat.js';
import { SIZE_BAND_OPTIONS, journeyLabel, journeyText, optionLabel, profileText } from './piQuery.js';
import { ORIGIN_PARAM, accountRoute, companyRoute } from './piNavigation.js';
import { listOf, objectOf } from './piRequests.js';
import { ACTIVE_30D_DESCRIPTION, countText, metricCell, missingActiveTitle, numberOf, ratioText } from './piTable.js';
import { moduleName } from './piFeaturesModel.js';
import { operationRows } from './piOperationsModel.js';
import { unavailableNotes } from './piState.js';
import { defineColumns } from './piColumns.js';
import { PLAN_OPTIONS } from './piOnboardingFilters.js';
import { DEMO_BADGE_TITLE, demoReasonLabel, demoRowText } from './piCompanyDemoModel.js';

export const DROP_DESCRIPTION = 'Ativos em 30 dias no último dia fechado até 70% do mesmo número no período anterior, com base de pelo menos 10 ativos.';

export const DEFAULT_COMPANY_SORT = { sort: 'activeUsers', desc: true };

export const SIGNAL_INFO = {
  drop: {
    label: 'Queda de atividade',
    tone: 'bad',
    description: DROP_DESCRIPTION,
  },
};

const STATUS_INFO = {
  active: { label: 'Ativa', tone: 'ok' },
  ativa: { label: 'Ativa', tone: 'ok' },
  trial: { label: 'Trial', tone: 'info' },
  inactive: { label: 'Inativa', tone: 'muted' },
  blocked: { label: 'Bloqueada', tone: 'warn' },
  expired: { label: 'Expirada', tone: 'warn' },
  canceled: { label: 'Cancelada', tone: 'muted' },
  cancelled: { label: 'Cancelada', tone: 'muted' },
};

const COMPANY_ID = /^[0-9A-Fa-f-]{1,40}$/;
const CONTEXT = /^[0-9A-Za-z]{1,8}$/;

const textOf = (value) => (value === null || value === undefined ? '' : String(value));

const filledText = (value) => {
  const text = textOf(value).trim();
  return text === EMPTY ? '' : text;
};

export const statusInfo = (status) => {
  const raw = textOf(status).trim();
  if (!raw) return { label: EMPTY, tone: 'muted' };
  const known = STATUS_INFO[raw.toLowerCase()];
  return known || { label: raw, tone: 'muted' };
};

export const signalInfo = (signal) => {
  const key = textOf(signal).toLowerCase();
  return Object.prototype.hasOwnProperty.call(SIGNAL_INFO, key) ? Object.assign({ key: key }, SIGNAL_INFO[key]) : null;
};

const signalLabel = (signal) => {
  const info = signalInfo(signal);
  return info ? info.label : '';
};

export const COMPANY_TABLE_COLUMNS = defineColumns([
  {
    key: 'name', label: 'Empresa', type: 'text', infoKey: 'common.company', sortable: true, startDescending: false, width: 40,
    value: (row) => filledText(objectOf(row).name) || filledText(objectOf(row).companyId),
  },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false, width: 34 },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  {
    key: 'profile', label: 'Porte, perfil e jornada', type: 'text', infoKey: 'companies.profile', export: false,
    exportedAs: ['sizeBand', 'segment', 'clientJourney'],
  },
  { key: 'sizeBand', label: 'Porte', type: 'text', infoKey: 'common.sizeBand', screen: false },
  { key: 'segment', label: 'Perfil da empresa', type: 'text', infoKey: 'common.segment', screen: false },
  { key: 'clientJourney', label: 'Jornada', type: 'text', infoKey: 'common.journey', screen: false, value: (row) => journeyLabel(objectOf(row).clientJourney) },
  { key: 'status', label: 'Situação', type: 'text', infoKey: 'common.companyStatus', value: (row) => filledText(statusInfo(objectOf(row).status).label) },
  {
    key: 'demo', label: 'Demonstração', type: 'text', infoKey: 'common.demo', export: false,
    exportedAs: ['isDemo', 'demoReason', 'demoSince', 'demoByName', 'suggestionReason'],
  },
  { key: 'isDemo', label: 'Demonstração', type: 'text', infoKey: 'common.demo', screen: false, value: (row) => objectOf(row).isDemo === true },
  {
    key: 'demoReason', label: 'Motivo da demonstração', type: 'text', infoKey: 'companies.demoReason', screen: false,
    value: (row) => (objectOf(row).isDemo === true ? demoReasonLabel(objectOf(row).demoReason) : ''),
  },
  { key: 'demoSince', label: 'Demonstração desde', type: 'date', infoKey: 'companies.demoSince', screen: false },
  { key: 'demoByName', label: 'Marcada por', type: 'text', infoKey: 'companies.demoBy', screen: false },
  { key: 'suggestionReason', label: 'Sugestão para revisar', type: 'text', infoKey: 'companies.demoSuggestion', screen: false },
  { key: 'lastActivityDate', label: 'Última atividade', type: 'date', infoKey: 'common.lastActivity', sortable: true, startDescending: true },
  {
    key: 'activeUsers', label: 'Ativos (30 dias)', type: 'int', infoKey: 'common.activeUsers30d', sortable: true, startDescending: true,
    description: ACTIVE_30D_DESCRIPTION, value: (row) => numberOf(objectOf(row).activeUsers),
  },
  { key: 'enabledUsers', label: 'Habilitados', type: 'int', infoKey: 'common.enabledUsers', screen: false, value: (row) => numberOf(objectOf(row).enabledUsers) },
  { key: 'activeSeatsRate', label: 'Active Seats Rate', type: 'metric', unit: 'pct', infoKey: 'common.activeSeatsRate', sortable: true, startDescending: true },
  {
    key: 'featuresAdopted', label: 'Funcionalidades adotadas', type: 'int', infoKey: 'companies.featuresAdopted', sortable: true, startDescending: true,
    value: (row) => numberOf(objectOf(row).featuresAdopted),
  },
  { key: 'trend', label: 'Tendência', type: 'text', infoKey: 'common.trend', export: false },
  { key: 'signal', label: 'Sinal', type: 'text', infoKey: 'companies.signal', value: (row) => signalLabel(objectOf(row).signal) },
]);

const DEMO_DETAIL_KEYS = ['demo', 'demoSince', 'demoByName'];

export const companyTableColumns = (internal) => (textOf(internal).trim()
  ? COMPANY_TABLE_COLUMNS
  : COMPANY_TABLE_COLUMNS.filter((column) => DEMO_DETAIL_KEYS.indexOf(column.key) === -1));

export const COMPANY_COLUMNS = COMPANY_TABLE_COLUMNS
  .filter((column) => column.sortable)
  .map((column) => {
    const legacy = { key: column.key, label: column.label, numeric: column.numeric, startDescending: column.startDescending };
    if (column.description) legacy.description = column.description;
    return legacy;
  });

export const validCompanySort = (sort) => COMPANY_TABLE_COLUMNS.some((column) => column.sortable && column.key === sort);

const ORDER_WORDS = {
  text: [', de A a Z', ', de Z a A'],
  date: [', da mais antiga para a mais recente', ', da mais recente para a mais antiga'],
};

export const companySortLine = (sort) => {
  const state = objectOf(sort);
  const column = COMPANY_TABLE_COLUMNS.filter((item) => item.sortable && item.key === state.sort)[0];
  if (!column) return '';
  const words = ORDER_WORDS[column.type] || [', do menor para o maior', ', do maior para o menor'];
  return 'Ordem: ' + column.label + words[state.desc === true ? 1 : 0];
};

export const parseCompanyParams = (companyId, context) => {
  const id = textOf(companyId).trim();
  const shard = textOf(context).trim();
  if (!COMPANY_ID.test(id) || !CONTEXT.test(shard)) return null;
  return { companyId: id, shardCode: shard };
};

const sizeBandText = (value) => (value ? optionLabel(SIZE_BAND_OPTIONS, textOf(value)) : EMPTY);

export const companyRow = (row, compare, index) => {
  const source = row || {};
  const signal = signalInfo(source.signal);
  const activeUsers = numberOf(source.activeUsers);
  const enabledUsers = numberOf(source.enabledUsers);
  const isDemo = source.isDemo === true;
  return {
    key: textOf(source.shardCode) + ':' + (textOf(source.companyId) || String(index || 0)),
    companyId: textOf(source.companyId),
    shardCode: textOf(source.shardCode),
    name: textOf(source.name) || textOf(source.companyId) || 'Empresa',
    route: companyRoute(source.companyId, source.shardCode),
    accountRoute: accountRoute(source.companyId, source.shardCode),
    sizeBand: textOf(source.sizeBand),
    sizeBandText: sizeBandText(source.sizeBand),
    segmentText: profileText(textOf(source.segment)) || EMPTY,
    journeyText: journeyText(source.clientJourney),
    status: statusInfo(source.status),
    lastActivityText: formatDateFull(source.lastActivityDate),
    activeText: countText(activeUsers),
    activeMissing: !isNumber(activeUsers),
    activeTitle: isNumber(activeUsers) ? '' : missingActiveTitle(source),
    usersText: isNumber(enabledUsers) ? 'de ' + formatInteger(enabledUsers) + ' habilitados' : '',
    activeSeatsRate: metricCell(source.activeSeatsRate, { compare: compare }),
    featuresText: countText(numberOf(source.featuresAdopted)),
    trend: listOf(source.trend),
    signal: signal,
    isDemo: isDemo,
    demoTitle: isDemo ? DEMO_BADGE_TITLE : '',
    demoText: demoRowText(source),
    suggestionText: isDemo ? '' : textOf(source.suggestionReason).trim(),
  };
};

export const companyRows = (rows, compare) => listOf(rows).map((row, index) => companyRow(row, compare, index));

export const signalCount = (rows) => listOf(rows).filter((row) => row.signal).length;

const nonNegative = (value) => (isNumber(value) && value >= 0 ? Math.floor(value) : null);

export const demoSummary = (data) => {
  const summary = objectOf(objectOf(data).summary);
  const demo = nonNegative(summary.demoCompanies);
  const suggested = nonNegative(summary.suggestedCompanies);
  return {
    known: demo !== null,
    demoCompanies: demo === null ? 0 : demo,
    suggestedCompanies: suggested === null ? 0 : suggested,
    demoText: demo === null ? '' : formatInteger(demo) + (demo === 1 ? ' empresa de demonstração fora dos números' : ' empresas de demonstração fora dos números'),
    suggestedText: suggested ? formatInteger(suggested) + (suggested === 1 ? ' sugestão para revisar' : ' sugestões para revisar') : '',
  };
};

export const companiesHint = (internal) => {
  const mode = textOf(internal).trim();
  if (mode === 'only') return 'só as empresas de demonstração ativas, sem atividade: elas ficam fora de todos os números do PI, inclusive do histórico';
  if (mode === 'suggested') return 'empresas ativas ainda sem a marca de demonstração que parecem nossas, para revisar uma a uma; nada é marcado sozinho';
  if (mode === 'include') return 'universo ativo e as empresas de demonstração, estas sem atividade';
  return 'universo: empresas ativas, sem as de demonstração; as de fora do universo com atividade no período aparecem sem ativos e sem taxa';
};

export const companiesEmptyText = (internal, search, filtered) => {
  const mode = textOf(internal).trim();
  if (textOf(search).trim()) return 'Nenhuma empresa encontrada para "' + textOf(search).trim() + '".';
  if (mode === 'only' && !filtered) return 'Nenhuma empresa marcada como demonstração.';
  if (mode === 'suggested' && !filtered) return 'Nenhuma sugestão para revisar.';
  return filtered ? 'Nenhuma empresa com esses filtros.' : 'Nenhuma empresa no recorte.';
};

export const findCompanyRow = (rows, companyId, shardCode) => {
  const id = textOf(companyId).toLowerCase();
  const shard = textOf(shardCode).toLowerCase();
  if (!id) return null;
  const list = listOf(rows);
  for (let i = 0; i < list.length; i++) {
    const row = list[i];
    if (textOf(row.companyId).toLowerCase() !== id) continue;
    if (shard && row.shardCode && textOf(row.shardCode).toLowerCase() !== shard) continue;
    return row;
  }
  return null;
};

export const peerComparison = (companyMetric, peers) => {
  const source = objectOf(peers);
  const company = metricCell(companyMetric, {});
  const peer = metricCell(source.medianActiveSeatsRate, {});
  let gapText = '';
  let gapTone = 'muted';
  if (company.visible && peer.visible) {
    const gap = company.metric.value - peer.metric.value;
    const size = formatDecimal(Math.abs(gap), 1) + ' p.p.';
    if (Math.abs(gap) < 0.05) gapText = 'Igual à mediana dos pares';
    else gapText = size + (gap > 0 ? ' acima da mediana dos pares' : ' abaixo da mediana dos pares');
    gapTone = gap < 0 ? 'warn' : 'ok';
  }
  return {
    company: company,
    peer: peer,
    sizeBand: textOf(source.sizeBand),
    sizeBandText: sizeBandText(source.sizeBand),
    gapText: gapText,
    gapTone: gapTone,
  };
};

export const COMPANY_PEER_COLUMNS = defineColumns([
  {
    key: 'company', label: 'Active Seats Rate da empresa', type: 'metric', unit: 'pct', infoKey: 'common.activeSeatsRate',
    value: (row) => objectOf(objectOf(row).company).metric,
  },
  {
    key: 'peer', label: 'Mediana dos pares', type: 'metric', unit: 'pct', infoKey: 'companies.peerMedian',
    value: (row) => objectOf(objectOf(row).peer).metric,
  },
  { key: 'sizeBand', label: 'Porte dos pares', type: 'text', infoKey: 'common.sizeBand', screen: false },
  {
    key: 'peerCount', label: 'Empresas do mesmo porte', type: 'int', infoKey: 'companies.peerCount', screen: false,
    value: (row) => {
      const metric = objectOf(objectOf(objectOf(row).peer).metric);
      return objectOf(objectOf(row).peer).visible === true && isNumber(metric.denominator) ? metric.denominator : null;
    },
  },
  { key: 'gapText', label: 'Distância da mediana dos pares', type: 'text', infoKey: 'companies.peerGap' },
]);

export const peerExportRows = (comparison) => (comparison && comparison.company ? [comparison] : []);

export const COMPANY_SERIES_COLUMNS = defineColumns([
  { key: 'date', label: 'Data', type: 'date', infoKey: 'companies.series.date', screen: false },
  { key: 'activeUsers', label: 'Usuários ativos', type: 'int', infoKey: 'companies.series.activeUsers', screen: false, value: (row) => numberOf(objectOf(row).activeUsers) },
  { key: 'inUseUsers', label: 'Usuários em uso', type: 'int', infoKey: 'companies.series.inUseUsers', screen: false, value: (row) => numberOf(objectOf(row).inUseUsers) },
  { key: 'punches', label: 'Batidas registradas', type: 'int', infoKey: 'companies.series.punches', screen: false, value: (row) => numberOf(objectOf(row).punches) },
  { key: 'isPartial', label: 'Parcial', type: 'text', infoKey: 'common.state.partial', screen: false, value: (row) => objectOf(row).isPartial === true },
]);

const featureName = (feature) => textOf(feature.name) || ('Funcionalidade ' + textOf(feature.featureType));

export const COMPANY_FEATURE_COLUMNS = defineColumns([
  { key: 'name', label: 'Funcionalidade', type: 'text', infoKey: 'companies.features.name', width: 36, value: (row) => featureName(objectOf(row)) },
  { key: 'module', label: 'Módulo', type: 'text', infoKey: 'companies.features.module', screen: false, value: (row) => moduleName(objectOf(row).module) },
  {
    key: 'users', label: 'Usuários', type: 'text', infoKey: 'companies.features.users', numeric: true, export: false,
    exportedAs: ['adoptingUsers', 'eligibleUsers'],
  },
  {
    key: 'adoptingUsers', label: 'Usuários que usaram', type: 'int', infoKey: 'companies.features.adoptingUsers', screen: false,
    value: (row) => numberOf(objectOf(row).adoptingUsers),
  },
  {
    key: 'eligibleUsers', label: 'Usuários elegíveis', type: 'int', infoKey: 'companies.features.eligibleUsers', screen: false,
    value: (row) => numberOf(objectOf(row).eligibleUsers),
  },
  { key: 'adoption', label: 'Adoção', type: 'metric', unit: 'pct', infoKey: 'companies.features.adoption' },
]);

export const COMPANY_OPERATION_COLUMNS = defineColumns([
  { key: 'label', label: 'Operação', type: 'text', infoKey: 'companies.operations.label', width: 36 },
  { key: 'attempts', label: 'Tentativas', type: 'int', infoKey: 'companies.operations.attempts' },
  { key: 'success', label: 'Sucessos', type: 'int', infoKey: 'companies.operations.success' },
  { key: 'businessFail', label: 'Falha funcional', type: 'int', infoKey: 'companies.operations.businessFail' },
  { key: 'technicalFail', label: 'Falha técnica', type: 'int', infoKey: 'companies.operations.technicalFail' },
  {
    key: 'successRate', label: 'Taxa de sucesso', type: 'metric', unit: 'pct', infoKey: 'companies.operations.successRate',
    value: (row) => objectOf(objectOf(row).successRate).metric,
  },
  { key: 'note', label: 'Observação', type: 'text', infoKey: 'companies.operations.note', screen: false, width: 50 },
]);

const sortedSeries = (series) => listOf(series)
  .filter((point) => typeof point.date === 'string' && point.date)
  .slice()
  .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

const planText = (plan) => {
  const raw = textOf(plan).trim();
  return raw ? optionLabel(PLAN_OPTIONS, raw) : EMPTY;
};

export const companyDetailView = (data, compare) => {
  const source = objectOf(data);
  const company = objectOf(source.company);
  const series = sortedSeries(source.series);
  return {
    company: {
      companyId: textOf(company.companyId),
      shardCode: textOf(company.shardCode),
      name: textOf(company.name) || textOf(company.companyId) || 'Empresa',
      sizeBand: textOf(company.sizeBand),
      sizeBandText: sizeBandText(company.sizeBand),
      segmentText: profileText(textOf(company.segment)) || EMPTY,
      journeyText: journeyLabel(company.clientJourney) || EMPTY,
      planText: planText(company.plan),
      createdText: formatDateFull(company.createdAt),
      status: statusInfo(company.status),
      seatsText: countText(numberOf(company.seatsContracted)),
      isDemo: company.isDemo === true,
      demo: company.demo && typeof company.demo === 'object' ? company.demo : null,
    },
    series: series,
    activitySeries: [
      { key: 'activeUsers', label: 'Usuários ativos', area: true, points: series.map((point) => ({ date: point.date, value: numberOf(point.activeUsers), partial: point.isPartial === true })) },
      { key: 'inUseUsers', label: 'Usuários em uso', points: series.map((point) => ({ date: point.date, value: numberOf(point.inUseUsers), partial: point.isPartial === true })) },
    ],
    punchSeries: [
      { key: 'punches', label: 'Batidas registradas', area: true, points: series.map((point) => ({ date: point.date, value: numberOf(point.punches), partial: point.isPartial === true })) },
    ],
    featureRows: listOf(source.features),
    features: listOf(source.features).map((feature, index) => ({
      key: textOf(feature.featureType) || String(index),
      featureType: feature.featureType,
      name: featureName(feature),
      moduleText: moduleName(feature.module),
      usersText: ratioText(numberOf(feature.adoptingUsers), numberOf(feature.eligibleUsers)),
      adoption: metricCell(feature.adoption, { compare: compare }),
    })),
    operations: operationRows(source.operations, compare),
    peers: objectOf(source.peers),
    insights: listOf(source.insights),
  };
};

export const COMPANY_FACTS = [
  { key: 'shardCode', label: 'Banco', infoKey: 'common.shard' },
  { key: 'sizeBandText', label: 'Porte', infoKey: 'common.sizeBand' },
  { key: 'segmentText', label: 'Perfil da empresa', infoKey: 'common.segment' },
  { key: 'journeyText', label: 'Jornada', infoKey: 'common.journey' },
  { key: 'planText', label: 'Plano', infoKey: 'companies.plan' },
  { key: 'createdText', label: 'Criada em', infoKey: 'companies.createdAt' },
  { key: 'seatsText', label: 'Assentos contratados', infoKey: 'common.seatsContracted' },
];

export const companyExportLine = (company, ids) => {
  const source = objectOf(company);
  const fallback = objectOf(ids);
  const id = textOf(source.companyId).trim() || textOf(fallback.companyId).trim();
  const shard = textOf(source.shardCode).trim() || textOf(fallback.shardCode).trim();
  const name = textOf(source.name).trim();
  const parts = [];
  if (name && name !== 'Empresa') parts.push(name);
  if (shard) parts.push('banco ' + shard);
  if (id) parts.push('identificador ' + id);
  return 'Empresa: ' + (parts.length ? parts.join(' · ') : EMPTY);
};

export const companyFacts = (company, fallbackShard) => {
  const source = objectOf(company);
  return COMPANY_FACTS.map((fact) => {
    let value = textOf(source[fact.key]).trim();
    if (fact.key === 'shardCode' && !value) value = textOf(fallbackShard).trim();
    return { key: fact.key, label: fact.label, infoKey: fact.infoKey, value: value || EMPTY };
  });
};

const COMPANY_BREAKDOWN_UNAVAILABLE = /funcionalidades e opera[cç][oõ]es[^.]*indispon[ií]ve/i;

export const companyBreakdownUnavailable = (envelope) => {
  const data = objectOf(envelope && envelope.data);
  const explicit = textOf(data.featuresUnavailableReason).trim();
  if (explicit) return explicit;
  return unavailableNotes(envelope).filter((note) => COMPANY_BREAKDOWN_UNAVAILABLE.test(note))[0] || '';
};

export const similarCompaniesQuery = (query, sizeBand) => {
  const next = {};
  Object.keys(objectOf(query)).forEach((key) => {
    if (key !== 'companyId' && key !== 'companyName' && key !== 'sizeBand' && key !== 'segment' && key !== ORIGIN_PARAM) next[key] = query[key];
  });
  if (/^[A-E]$/.test(textOf(sizeBand))) next.sizeBand = textOf(sizeBand);
  return next;
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piCompaniesModel.js